import React, { FC, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import classNames from "classnames";
import { PdpScoreReportDialog } from "../../../../../components/PdpScoreStatus/PdpScoreReportDialog";
import { scoreCls } from "../../../../../components/PdpScoreStatus/PdpScoreStatus";
import type { PdpUrlVideoLink } from "../../../../../components/PdpPages/PdpJsonLdOutput";
import { useUser } from "../../../../../hooks/useUser";
import { isInternal } from "../../../../../utils";
import { CountPill, IconAlertGlyph, IconChartGlyph } from "../../../videos/selectedVideoCardV2/panels";
import { PrimaryButton, RetryButton, ScoreState } from "../../../videos/selectedVideoCardV2/performance/ScoreState";
import { canScoreSync } from "../../../videos/selectedVideoCardV2/performance/ScoreNowAction";
import type { ISingleProduct } from "../../../productsV2/productListView/constants";
import { glyph } from "../components/ui";
import { STATE_BLOCK_WITH_ACTION, SectionCard, StateBlockSkeleton } from "../components/SectionCard";
import { SECTION_IDS } from "../components/SectionRail";
import { readerStillIn, useRetryFocus } from "../components/focusReturn";
import { SegmentedTabs, tabId } from "../components/SegmentedTabs";
import type { SegmentedTab } from "../components/SegmentedTabs";
import { TouchTarget } from "../components/TouchTarget";
import { useMountedRef } from "../components/useMountedRef";
import { AddPageForm } from "./AddPageForm";
import { DeletePageConfirm, RemoveVideoConfirm } from "./ConfirmDialogs";
import { EditPageDialog } from "./EditPageDialog";
import { removeVideoId, videosHeadingId } from "./OurVideosRow";
import { PageBlock, pageBlockId } from "./PageBlock";
import { pageAfterDelete, pageTabItems, sectionView } from "./pageModel";
import type { PdpUrl } from "./pageModel";
import { useBusyKeys } from "./useBusyKeys";
import { TAB_PREFIX, usePageTabPick } from "./usePageTabPick";
import { useProductPages } from "./useProductPages";

/**
 * Section 2, Product pages & audits (D1) — replaces the modal's PDP 1/2/3
 * tabs (MODAL/ProductPdpUrlsPanel.tsx:700-733).
 *
 * Always in the SectionCard (the video details page's panel chrome). Views
 * (`sectionView`), the empty and problem states in the video page's Page score
 * vocabulary (ScoreState, PageScorePanel.tsx:355-430):
 *   loading   a block-sized skeleton
 *   error     the error block + Retry, announced (role="alert") — never the
 *             empty block
 *   none      "No product page registered" + a primary "Add a page" (the
 *             video page's "Add this page & score it" slot); it opens the form
 *   card      a count pill at 2+ and the "Add a page" pill in the header; in
 *             the body the form (when open — with no pages too), an "Added,
 *             but paused" note, then:
 *               1 page    its PageBlock, as it is — no tabs, no tab roles
 *               2+ pages  a tab per page (SegmentedTabs, oldest first: the
 *                         site + its score, `pageTabItems`) and the chosen
 *                         page's block in its tab panel
 *
 * Tabs (the user's call, 2026-09-29 — the handoff stacked the pages; the
 * pick, the hash and the tab row's scroll are usePageTabPick.ts):
 *   - which page shows is `resolvePageTab(ids, picked)`: the pick while it is
 *     listed, else the first. The pick starts from PAGE_TAB_PARAM in the URL
 *     (a refresh or a shared link opens that page; a bad id shows the first),
 *     moves to a page just added (`lastAddedId`, once listed), and after a
 *     delete to the page after it, else the one before. A page that goes any
 *     other way (deleted in Settings) falls back to the first.
 *   - every panel stays MOUNTED; the others are `hidden` (out of the page and
 *     the accessibility tree — so the blocks' identical button names never
 *     meet): a switch refetches no history, closes no open row, and a Score
 *     now keeps its elapsed counter. The one-at-a-time lock holds across
 *     tabs: the other pages' Score now stays disabled, with LOCKED_NOTE.
 *   - the pick is written to the URL's hash (`#pdpUrlId=151`, PAGE_TAB_PARAM:
 *     why the hash, not the query, is there) with history.replaceState, NOT
 *     the router: the Videos list refetches on router search changes
 *     (useFetchVideos.ts:308-310; useVideosQuery's key), so a router write
 *     would reload the grid on every tab click. The router keeps its own
 *     state in history.state (key, idx), left as is. A router navigation (a
 *     video opened or closed, a Videos filter) writes a URL without the
 *     hash; the effect on useLocation() puts the pick back. It is there only
 *     while tabs show and the pick isn't the first page (`pageTabParam`),
 *     only on this product's URL, never written on unmount; leaving the
 *     product is a router navigation, which drops it.
 *
 * Focus never drops to <body> after the section's own changes (#13): Cancel
 * returns to the control that opened the form, an add to the new page's tab
 * (or its block, at 1 page), a delete to the next page's tab, a removed video
 * to the next video's Remove from page, a Retry to what replaced it.
 *
 * The page wires it as
 *   <ProductPagesSection product={product} reloadKey={sectionsNonce}
 *     pollPaused={settingsOpen} onCount={setPagesCount} className={offWhileVideo} />
 * `reloadKey` bumps re-read the list in place (a page added in the settings
 * modal appears); `pollPaused` holds the pending poll while the modal runs
 * its own; `onCount` feeds the rail's "Product pages" chip — the page count,
 * null while unknown.
 *
 * The section owns the dialogs the modal owns: the edit dialog, the delete and
 * remove confirmations (ConfirmDialogs.tsx, copied, :872-891) and the audit
 * report.
 */

const TITLE = "Product pages & audits";
const panelId = (id: number) => `${TAB_PREFIX}-panel-${id}`;
const NO_ROWS: PdpUrl[] = [];

/**
 * After the section's own change took focus with it: the page's tab (2+
 * pages) or its block (1 page), else the section's first button — the
 * header's Add a page, the empty block's Add a page, or Retry.
 */
const focusSection = (pageId: number | null) => {
  const page =
    pageId != null
      ? document.getElementById(tabId(TAB_PREFIX, String(pageId))) || document.getElementById(pageBlockId(pageId))
      : null;
  const target = page || document.getElementById(SECTION_IDS.pages)?.querySelector<HTMLElement>("button");
  target?.focus();
};

/** The busy key of a confirmation's write — per item, so a second dialog isn't shown busy by the first's call. */
const deleteKey = (row: PdpUrl) => `page:${row.id}`;
const unlinkKey = (row: PdpUrl, link: PdpUrlVideoLink) => `video:${row.id}:${link.s3_file_id}`;

const plus = (size: number) => (
  <span className="inline-flex" aria-hidden="true">
    {glyph("plus", size, 2.4)}
  </span>
);

/**
 * The header action pill (README:84): 30px, 44px below md. `held` while an
 * add is out: closing the form then would unmount it, and the add's error
 * (shown in the form, not toasted) would be lost. Off = btnClass's QUIET:
 * --surface-2 / --muted.
 */
const AddPagePill: FC<{ formOpen: boolean; held: boolean; onClick: () => void }> = ({ formOpen, held, onClick }) => (
  <button
    type="button"
    aria-expanded={formOpen}
    onClick={onClick}
    disabled={held}
    className={classNames(
      "inline-flex items-center gap-1.5 h-[30px] max-md:h-11 px-3 rounded-full bg-surface-2 whitespace-nowrap shrink-0 transition-colors",
      held ? "text-muted cursor-not-allowed" : "text-text-1 hover:bg-track",
    )}
    style={{ fontSize: 12.5 }}
  >
    {plus(12)}
    Add a page
  </button>
);

interface Props {
  /** The header payload — the same object the page hands the settings modal. */
  product: ISingleProduct;
  /** Bump → a silent reload of the list (and of any page history whose last load failed). */
  reloadKey: number;
  /** The settings modal is open: its own pending poll runs, so the page's holds. */
  pollPaused?: boolean;
  /** The page count for the rail chip; null while unknown (loading, error). */
  onCount?: (n: number | null) => void;
  /** The page's `hidden` while `?video=` is open (Q1 b). */
  className?: string;
}

export const ProductPagesSection: FC<Props> = ({ product, reloadKey, pollPaused = false, onCount, className }) => {
  const { role } = useUser();
  const sync = canScoreSync(role);
  const internal = isInternal(role);
  const productId = product.desireid;
  const pages = useProductPages(productId, reloadKey, pollPaused);
  const { rows, loading, error, scoringPageId, scoringStartedAt, refusals, isBusy } = pages;

  const [formOpen, setFormOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<PdpUrl | null>(null);
  // The delete confirmation's page, with its tab label as it was when asked.
  const [rowToDelete, setRowToDelete] = useState<{ row: PdpUrl; label: string } | null>(null);
  const [linkToRemove, setLinkToRemove] = useState<{ row: PdpUrl; link: PdpUrlVideoLink } | null>(null);
  // Confirmation writes in flight, by item (deleteKey / unlinkKey).
  const { busy: busyKeys, markBusy } = useBusyKeys();
  const [viewingReport, setViewingReport] = useState<{ pdpUrlId: number; scoreId: number; pdpUrl: string } | null>(null);
  const mounted = useMountedRef();

  const view = sectionView(rows, formOpen, { loading, error });
  const list = rows || NO_ROWS;
  // Kept across re-renders that don't change the list (a busy flag, a dialog
  // opening): the ids the tab rules read and the tab row's items.
  const ids = useMemo(() => list.map((r) => r.id), [list]);
  const tabs = list.length >= 2;
  const tabItems = useMemo(() => pageTabItems(list), [list]);
  const segmentedTabs = useMemo<SegmentedTab[]>(
    () =>
      tabItems.map((t) => ({
        id: String(t.id),
        panelId: panelId(t.id),
        ariaLabel: t.ariaLabel,
        title: t.title,
        label: (
          <>
            <span className="truncate min-w-0" style={{ maxWidth: 220 }}>
              {t.label}
            </span>
            {t.score != null && (
              <span className={classNames("font-CircularStdBlack tabular-nums", scoreCls(t.score))}>{t.score}</span>
            )}
          </>
        ),
      })),
    [tabItems],
  );
  const labelOf = (id: number) => tabItems.find((t) => t.id === id)?.label || "";

  // What an async handler reads after its await: this render's values, not
  // the ones its closure was made with.
  const latest = useRef({ rowToDelete, linkToRemove, lastAddedId: pages.lastAddedId });
  latest.current = { rowToDelete, linkToRemove, lastAddedId: pages.lastAddedId };

  const count = !loading && !error && rows ? rows.length : null;
  useEffect(() => {
    onCount?.(count);
  }, [count, onCount]);

  // ---- which page's tab shows (usePageTabPick.ts) ----
  const { picked, setPicked, shownId } = usePageTabPick({
    productId,
    ids,
    listed: rows != null,
    tabs,
    view,
    lastAddedId: pages.lastAddedId,
    className,
  });

  // ---- focus, after the render a change causes ----
  const focusAim = useRef<(() => void) | null>(null);
  const [focusTick, setFocusTick] = useState(0);
  const focusAfterRender = (aim: () => void) => {
    if (!mounted.current) return;
    focusAim.current = aim;
    setFocusTick((n) => n + 1);
  };
  useEffect(() => {
    const aim = focusAim.current;
    focusAim.current = null;
    aim?.();
  }, [focusTick]);

  // Retry swaps the error block for the skeleton, and its button (focused)
  // goes with it: once the section lands, focus goes to what replaced it —
  // Retry again, the empty block's Add a page, or the shown page — unless
  // the reader has moved on (`readerStillIn`).
  const markRetry = useRetryFocus(view !== "loading", () => {
    if (readerStillIn(SECTION_IDS.pages)) focusSection(view === "card" ? shownId : null);
  });
  const retry = () => {
    markRetry();
    pages.reload();
  };

  const openForm = useCallback(() => setFormOpen(true), []);
  // Cancel: back to the opener — the header pill, or (no pages) the empty
  // block's button, which returns as the form closes. Both are the section's
  // first button.
  const cancelForm = () => {
    setFormOpen(false);
    focusAfterRender(() => focusSection(null));
  };
  // Added: `add` resolved after `lastAddedId` named the page, and
  // usePageTabPick has picked it. Its tab (or, as the only page, its block) — unless
  // the reader moved on while the add was out.
  const formAdded = () => {
    if (!mounted.current) return;
    setFormOpen(false);
    focusAfterRender(() => {
      if (readerStillIn(SECTION_IDS.pages)) focusSection(latest.current.lastAddedId);
    });
  };

  // handleDeleteRow (:403-416): the dialog closes as soon as the DELETE
  // succeeds (the re-read runs behind, in the hook), and only if it still
  // asks about that page — one reopened on another page meanwhile stays open
  // and isn't shown busy. The page on screen moves to the next one, worked
  // out now: the row has left `rows` by the time the call resolves.
  const confirmDelete = async () => {
    const row = rowToDelete?.row;
    if (!row || busyKeys[deleteKey(row)]) return;
    const wasShown = shownId === row.id;
    const next = pageAfterDelete(ids, row.id);
    const pickedAtAsk = picked;
    markBusy(deleteKey(row), true);
    const ok = await pages.deleteRow(row);
    if (!mounted.current) return;
    markBusy(deleteKey(row), false);
    if (!ok) return;
    // A tab the reader picked meanwhile stands.
    if (wasShown) setPicked((cur) => (cur === pickedAtAsk ? next : cur));
    if (latest.current.rowToDelete?.row.id === row.id) {
      setRowToDelete(null);
      // The dialog's focus return aims at the deleted page's ⋮, now gone.
      focusAfterRender(() => focusSection(next));
    }
  };

  // handleUnlinkVideo (:420-433): the same, per video.
  const confirmUnlink = async () => {
    const ask = linkToRemove;
    if (!ask) return;
    const key = unlinkKey(ask.row, ask.link);
    if (busyKeys[key]) return;
    const links = list.find((r) => r.id === ask.row.id)?.videos || [];
    const at = links.findIndex((l) => Number(l.s3_file_id) === Number(ask.link.s3_file_id));
    const neighbour = at < 0 ? null : links[at + 1] || links[at - 1] || null;
    markBusy(key, true);
    const ok = await pages.unlinkVideo(ask.row, ask.link);
    if (!mounted.current) return;
    markBusy(key, false);
    if (!ok) return;
    const asking = latest.current.linkToRemove;
    if (asking && unlinkKey(asking.row, asking.link) === key) {
      setLinkToRemove(null);
      // The removed video's button went with it: the next one's, else the row heading.
      const blockId = pageBlockId(ask.row.id);
      focusAfterRender(() => {
        const target =
          (neighbour && document.getElementById(removeVideoId(blockId, neighbour.s3_file_id))) ||
          document.getElementById(videosHeadingId(blockId));
        if (target) target.focus();
        else focusSection(ask.row.id);
      });
    }
  };

  // ---- the section, by view ----
  const card = (children: ReactNode, extra?: { count?: ReactNode; action?: ReactNode; bodyClassName?: string }) => (
    <SectionCard id={SECTION_IDS.pages} title={TITLE} className={className} {...extra}>
      {children}
    </SectionCard>
  );

  let section: ReactNode;
  if (view === "loading") {
    // Sized like the empty block it most often becomes (it has a button).
    section = card(<StateBlockSkeleton minHeight={STATE_BLOCK_WITH_ACTION} />);
  } else if (view === "error") {
    // Announced: ScoreState (shared) has no live region of its own. The
    // buttons are 44px below md (TouchTarget), as every control on the page.
    section = card(
      <div role="alert">
        <ScoreState
          skin="error"
          icon={<IconAlertGlyph size={20} strokeWidth={1.7} />}
          title="Couldn't load product pages"
          body="Something went wrong on our end."
          action={
            <TouchTarget>
              <RetryButton onClick={retry} />
            </TouchTarget>
          }
        />
      </div>,
    );
  } else if (view === "none") {
    section = card(
      <ScoreState
        skin="empty"
        icon={<IconChartGlyph size={20} strokeWidth={1.6} />}
        title="No product page registered"
        body="Add the page that sells this product to see how it scores."
        action={
          <TouchTarget>
            <PrimaryButton onClick={openForm} icon={plus(13)}>
              Add a page
            </PrimaryButton>
          </TouchTarget>
        }
      />,
    );
  } else {
    const addedPaused =
      pages.addedPausedId != null && list.some((r) => r.id === pages.addedPausedId && r.active === false);
    section = card(
      <>
        {formOpen && <AddPageForm product={product} onCancel={cancelForm} onAdd={pages.add} onAdded={formAdded} />}
        {addedPaused && (
          <div
            role="status"
            className="flex items-center gap-2.5 rounded-lg bg-page px-3.5 py-2.5 text-text-2"
            style={{ fontSize: 12.5 }}
          >
            <span className="inline-flex shrink-0" aria-hidden="true">
              {glyph("info", 15, 2)}
            </span>
            <span>
              <span className="text-text-1">Added, but paused:</span> the plan is at its scoring limit.
            </span>
          </div>
        )}
        {tabs && shownId != null && (
          // Content-width (self-start), scrolling sideways when the tabs
          // outgrow the card (SegmentedTabs), as at 375.
          <SegmentedTabs
            idPrefix={TAB_PREFIX}
            ariaLabel="Product pages"
            className="self-start"
            selectedId={String(shownId)}
            onSelect={(id) => setPicked(Number(id))}
            items={segmentedTabs}
          />
        )}
        {/* Every page in the same wrapper at any count, so going from one
            page to two (or back) never remounts a block. */}
        {list.map((row) => (
          <div
            key={row.id}
            role={tabs ? "tabpanel" : undefined}
            id={tabs ? panelId(row.id) : undefined}
            aria-labelledby={tabs ? tabId(TAB_PREFIX, String(row.id)) : undefined}
            hidden={tabs && row.id !== shownId}
            className="min-w-0"
          >
            <PageBlock
              row={row}
              productId={productId}
              sync={sync}
              internal={internal}
              reloadKey={reloadKey}
              scoringPageId={scoringPageId}
              scoringStartedAt={scoringStartedAt}
              refusal={refusals[row.id]}
              requesting={isBusy("request", row.id)}
              toggling={isBusy("toggle", row.id)}
              generating={isBusy("generate", row.id)}
              generatingProduct={isBusy("generateProduct", row.id)}
              onScore={() => pages.score(row, sync)}
              onToggleScoring={() => pages.toggleScoring(row)}
              onEdit={() => setEditingRow(row)}
              onDelete={() => setRowToDelete({ row, label: labelOf(row.id) })}
              onRemoveVideo={(link) => setLinkToRemove({ row, link })}
              onViewAudit={(scoreId) => setViewingReport({ pdpUrlId: row.id, scoreId, pdpUrl: row.pdp_url })}
              onGenerate={() => pages.generateJsonLd(row)}
              onGenerateProduct={() => pages.generateProductJsonLd(row)}
            />
          </div>
        ))}
      </>,
      {
        count: list.length >= 2 ? <CountPill value={list.length} /> : undefined,
        action: (
          <AddPagePill
            formOpen={formOpen}
            held={pages.adding}
            // Closing is Cancel: with no pages this pill goes with the form,
            // so focus goes back to the empty block's Add a page.
            onClick={() => (formOpen ? cancelForm() : openForm())}
          />
        ),
        bodyClassName: "flex flex-col gap-4",
      },
    );
  }

  const deleting = !!rowToDelete && !!busyKeys[deleteKey(rowToDelete.row)];
  const unlinking = !!linkToRemove && !!busyKeys[unlinkKey(linkToRemove.row, linkToRemove.link)];

  return (
    <>
      {section}

      {/* Opened on the row as it is then; the saved values patch the row at
          once (reopening shows them) and the re-read runs behind. */}
      <EditPageDialog
        productId={productId}
        row={editingRow}
        onClose={() => setEditingRow(null)}
        onSaved={(id, saved) => {
          pages.patchRow(id, saved);
          pages.silentFetch();
        }}
      />

      {viewingReport && (
        <PdpScoreReportDialog
          productId={productId}
          pdpUrlId={viewingReport.pdpUrlId}
          scoreId={viewingReport.scoreId}
          pdpUrl={viewingReport.pdpUrl}
          onClose={() => setViewingReport(null)}
        />
      )}

      {/* Copied from :872-891 (ConfirmDialogs.tsx), the delete naming the
          page by its tab's label. `disabled` while that item's call is out. */}
      <DeletePageConfirm ask={rowToDelete} busy={deleting} onClose={() => setRowToDelete(null)} onConfirm={confirmDelete} />
      <RemoveVideoConfirm ask={linkToRemove} busy={unlinking} onClose={() => setLinkToRemove(null)} onConfirm={confirmUnlink} />
    </>
  );
};
